/**
 * OfflineQueueManager - Buffers write operations during network outages and replays when connection restores
 * Handles bookmarks, claps, comments, and story updates with conflict resolution
 * Enhanced with batch processing and compression for optimized replay
 */

import { v4 as uuidv4 } from 'uuid';
import { errorLogger } from './errorLogger';
import { showToast } from './toast';
import { CONFLICT_STRATEGIES, detectConflict, isOperationStale, logConflictResolution, generateOperationFingerprint,  } from './conflictResolution';
import { compressData, decompressData, compressText, decompressText } from './compressionUtils';
import { batchOperations } from './batchOperations';
import { metricsCollector } from './queueMetricsCollector';

const QUEUE_STORAGE_KEY = 'quilline_offline_queue';
const MAX_QUEUE_SIZE = 100;
const REPLAY_BATCH_SIZE = 5; // Process 5 operations at a time
const STALE_THRESHOLD_MS = 300000; // 5 minutes
const MAX_BATCH_SIZE = 50; // Maximum operations per batch to prevent timeouts

export const OPERATION_TYPES = {
  BOOKMARK: 'bookmark',
  CLAP: 'clap',
  COMMENT: 'comment',
  STORY_UPDATE: 'story_update',
};

export const OPERATION_ACTIONS = {
  INSERT: 'insert',
  UPDATE: 'update',
  DELETE: 'delete',
};

export const QUEUE_STATUS = {
  PENDING: 'pending',
  REPLAYING: 'replaying',
  FAILED: 'failed',
};

/**
 * OfflineQueueManager - Manages offline operation queue
 */
class OfflineQueueManager {
  constructor() {
    this.queue = [];
    this.isOnline = navigator?.onLine;
    this.isReplaying = false;
    this.listeners = [];
    this.connectionQualityGood = true;
    this.batchProgress = {
      isActive: false,
      currentBatch: 0,
      totalBatches: 0,
      currentType: null,
      processedOps: 0,
      totalOps: 0,
      stats: null,
      lastResult: null,
    };
    
    // Load queue from storage
    this.loadQueue();
    
    // Setup online/offline listeners
    this.setupNetworkListeners();
    
    // Check connection quality periodically
    this.startConnectionQualityCheck();
  }

  /**
   * Setup network event listeners
   */
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      console.log('[OfflineQueue] Network online');
      this.isOnline = true;
      this.notifyListeners();
      this.replayQueue();
    });

    window.addEventListener('offline', () => {
      console.log('[OfflineQueue] Network offline');
      this.isOnline = false;
      this.notifyListeners();
    });
  }

  /**
   * Check connection quality using a lightweight ping
   */
  async checkConnectionQuality() {
    if (!navigator?.onLine) {
      this.connectionQualityGood = false;
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller?.abort(), 3000);
      
      const response = await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller?.signal,
      });
      
      clearTimeout(timeoutId);
      this.connectionQualityGood = true;
      return true;
    } catch (error) {
      this.connectionQualityGood = false;
      return false;
    }
  }

  /**
   * Start periodic connection quality checks
   */
  startConnectionQualityCheck() {
    setInterval(() => {
      this.checkConnectionQuality();
    }, 30000); // Check every 30 seconds
  }

  /**
   * Load queue from localStorage with decompression
   */
  loadQueue() {
    try {
      const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
      if (stored) {
        // Try to decompress first
        const decompressed = decompressData(stored);
        if (decompressed) {
          this.queue = decompressed;
          console.log(`[OfflineQueue] Loaded ${this.queue?.length} operations from storage (compressed)`);
        } else {
          // Fallback to JSON parse
          this.queue = JSON.parse(stored);
          console.log(`[OfflineQueue] Loaded ${this.queue?.length} operations from storage`);
        }
      }
    } catch (error) {
      console.error('[OfflineQueue] Failed to load queue:', error);
      this.queue = [];
    }
  }

  /**
   * Save queue to localStorage with compression
   */
  saveQueue() {
    try {
      const compressed = compressData(this.queue);
      localStorage.setItem(QUEUE_STORAGE_KEY, compressed);
      
      // Log compression stats occasionally
      if (this.queue?.length > 0 && Math.random() < 0.1) {
        const originalSize = JSON.stringify(this.queue)?.length;
        const compressedSize = compressed?.length;
        const ratio = ((1 - compressedSize / originalSize) * 100)?.toFixed(1);
        console.log(`[OfflineQueue] Compression saved ${ratio}% space (${originalSize} → ${compressedSize} bytes)`);
      }
    } catch (error) {
      console.error('[OfflineQueue] Failed to save queue:', error);
      errorLogger?.logError(error, {
        component: 'OfflineQueueManager',
        operation: 'saveQueue',
      });
    }
  }

  /**
   * Add operation to queue with enhanced metadata and compression
   */
  enqueue(operationType, tableName, operation, data, metadata = {}) {
    // Check queue size limit
    if (this.queue?.length >= MAX_QUEUE_SIZE) {
      // Remove oldest pending operation
      const oldestIndex = this.queue?.findIndex(op => op?.status === QUEUE_STATUS?.PENDING);
      if (oldestIndex !== -1) {
        this.queue?.splice(oldestIndex, 1);
        console.warn('[OfflineQueue] Queue full, removed oldest operation');
      }
    }

    // Compress large text fields (comments, story content)
    let compressedData = { ...data };
    let compressionFlags = {};

    if (operationType === OPERATION_TYPES?.COMMENT && data?.content) {
      const { text, isCompressed } = compressText(data?.content);
      compressedData.content = text;
      compressionFlags.contentCompressed = isCompressed;
    } else if (operationType === OPERATION_TYPES?.STORY_UPDATE && data?.content) {
      const { text, isCompressed } = compressText(data?.content);
      compressedData.content = text;
      compressionFlags.contentCompressed = isCompressed;
    }

    // Generate operation fingerprint for duplicate detection
    const fingerprint = generateOperationFingerprint(
      operationType,
      data?.user_id || data?.author_id,
      data?.story_id || data?.id,
      data
    );

    // Check for duplicates in queue
    const duplicateResult = CONFLICT_STRATEGIES?.skipDuplicates(
      { operationType, data, metadata },
      this.queue
    );

    if (duplicateResult?.isDuplicate) {
      console.log(`[OfflineQueue] Duplicate operation detected, merging with ${duplicateResult?.duplicateOf}`);
      
      // Merge with existing operation
      const existingOp = this.queue?.find(op => op?.id === duplicateResult?.duplicateOf);
      if (existingOp && operationType === OPERATION_TYPES?.CLAP) {
        // Merge clap counts
        existingOp.metadata.clapIncrement = (existingOp?.metadata?.clapIncrement || 1) + (metadata?.clapIncrement || 1);
        this.saveQueue();
        this.notifyListeners();
        return existingOp?.id;
      } else if (existingOp && operationType === OPERATION_TYPES?.BOOKMARK) {
        // Update bookmark state to latest
        existingOp.data = compressedData;
        existingOp.timestamp = Date.now();
        existingOp.lastModified = Date.now();
        existingOp.compressionFlags = compressionFlags;
        this.saveQueue();
        this.notifyListeners();
        return existingOp?.id;
      } else if (existingOp && operationType === OPERATION_TYPES?.COMMENT) {
        // Skip duplicate comment
        console.log('[OfflineQueue] Duplicate comment, skipping');
        return existingOp?.id;
      }
    }

    const queueItem = {
      id: uuidv4(),
      operationType,
      tableName,
      operation,
      data: compressedData,
      metadata,
      compressionFlags,
      timestamp: Date.now(),
      queuedAt: Date.now(),
      lastModified: Date.now(),
      retryCount: 0,
      status: QUEUE_STATUS?.PENDING,
      fingerprint,
      version: 1, // Version tracking for stale detection
      conflictResolutionStrategy: this.getConflictStrategy(operationType),
      originalServerState: null, // Will be populated during replay
    };

    this.queue?.push(queueItem);
    this.saveQueue();
    this.notifyListeners();

    console.log(`[OfflineQueue] Enqueued ${operationType} operation:`, queueItem?.id);

    // Try to replay immediately if online
    if (this.isOnline && this.connectionQualityGood) {
      this.replayQueue();
    }

    return queueItem?.id;
  }

  /**
   * Get conflict resolution strategy for operation type
   */
  getConflictStrategy(operationType) {
    switch (operationType) {
      case OPERATION_TYPES?.BOOKMARK:
        return 'server_state_priority';
      case OPERATION_TYPES?.CLAP:
        return 'merge_numeric';
      case OPERATION_TYPES?.COMMENT:
        return 'skip_duplicates';
      case OPERATION_TYPES?.STORY_UPDATE:
        return 'last_write_wins';
      default:
        return 'last_write_wins';
    }
  }

  /**
   * Remove operation from queue
   */
  dequeue(operationId) {
    const index = this.queue?.findIndex(op => op?.id === operationId);
    if (index !== -1) {
      this.queue?.splice(index, 1);
      this.saveQueue();
      this.notifyListeners();
      return true;
    }
    return false;
  }

  /**
   * Get pending operations count
   */
  getPendingCount() {
    return this.queue?.filter(op => op?.status === QUEUE_STATUS?.PENDING)?.length;
  }

  /**
   * Get all operations
   */
  getQueue() {
    return [...this.queue];
  }

  /**
   * Clear all operations
   */
  clearQueue() {
    this.queue = [];
    this.saveQueue();
    this.notifyListeners();
    console.log('[OfflineQueue] Queue cleared');
  }

  /**
   * Clear failed operations
   */
  clearFailedOperations() {
    this.queue = this.queue?.filter(op => op?.status !== QUEUE_STATUS?.FAILED);
    this.saveQueue();
    this.notifyListeners();
  }

  /**
   * Replay queued operations with batch processing and compression
   */
  async replayQueue() {
    if (this.isReplaying) {
      console.log('[OfflineQueue] Already replaying, skipping');
      return;
    }

    if (!this.isOnline || !this.connectionQualityGood) {
      console.log('[OfflineQueue] Offline or poor connection, skipping replay');
      return;
    }

    let pendingOps = this.queue?.filter(op => op?.status === QUEUE_STATUS?.PENDING);
    if (pendingOps?.length === 0) {
      return;
    }

    this.isReplaying = true;
    console.log(`[OfflineQueue] Starting batch replay for ${pendingOps?.length} operations`);

    // Track replay start time
    const replayStartTime = Date.now();

    // Batch operations for optimized replay
    const batched = batchOperations(pendingOps);
    
    // Initialize batch progress
    this.batchProgress = {
      isActive: true,
      currentBatch: 0,
      totalBatches: batched?.stats?.batchCount,
      currentType: null,
      processedOps: 0,
      totalOps: batched?.stats?.optimizedCount,
      stats: batched?.stats,
      lastResult: null,
    };
    this.notifyListeners();

    let successCount = 0;
    let failCount = 0;
    let skippedCount = 0;
    let conflictCount = 0;

    const startTime = Date.now();

    try {
      // Import supabase dynamically
      const { retryableSupabase } = await import('../lib/supabase');

      // Replay clap batches
      for (let i = 0; i < batched?.claps?.length; i++) {
        this.batchProgress.currentBatch++;
        this.batchProgress.currentType = 'claps';
        this.notifyListeners();

        const batchStartTime = Date.now();
        const result = await this.replayBatchedClaps(retryableSupabase, batched?.claps?.[i]);
        const batchEndTime = Date.now();
        
        // Track replay duration for this batch
        metricsCollector?.trackReplayDuration(
          OPERATION_TYPES?.CLAP,
          batchStartTime,
          batchEndTime,
          batched?.claps?.[i]?.operations?.length,
          result?.success > 0
        );
        
        successCount += result?.success;
        failCount += result?.failed;
        this.batchProgress.processedOps += result?.success + result?.failed;
        this.notifyListeners();
      }

      // Replay bookmark batches
      for (let i = 0; i < batched?.bookmarks?.length; i++) {
        this.batchProgress.currentBatch++;
        this.batchProgress.currentType = 'bookmarks';
        this.notifyListeners();

        const batchStartTime = Date.now();
        const result = await this.replayBatchedBookmarks(retryableSupabase, batched?.bookmarks?.[i]);
        const batchEndTime = Date.now();
        
        // Track replay duration for this batch
        metricsCollector?.trackReplayDuration(
          OPERATION_TYPES?.BOOKMARK,
          batchStartTime,
          batchEndTime,
          batched?.bookmarks?.[i]?.operations?.length,
          result?.success > 0
        );
        
        successCount += result?.success;
        failCount += result?.failed;
        this.batchProgress.processedOps += result?.success + result?.failed;
        this.notifyListeners();
      }

      // Replay comment batches
      for (let i = 0; i < batched?.comments?.length; i++) {
        this.batchProgress.currentBatch++;
        this.batchProgress.currentType = 'comments';
        this.notifyListeners();

        const batchStartTime = Date.now();
        const result = await this.replayBatchedComments(retryableSupabase, batched?.comments?.[i]);
        const batchEndTime = Date.now();
        
        // Track replay duration for this batch
        metricsCollector?.trackReplayDuration(
          OPERATION_TYPES?.COMMENT,
          batchStartTime,
          batchEndTime,
          batched?.comments?.[i]?.operations?.length,
          result?.success > 0
        );
        
        successCount += result?.success;
        failCount += result?.failed;
        this.batchProgress.processedOps += result?.success + result?.failed;
        this.notifyListeners();
      }

      // Replay story update batches
      for (let i = 0; i < batched?.storyUpdates?.length; i++) {
        this.batchProgress.currentBatch++;
        this.batchProgress.currentType = 'story updates';
        this.notifyListeners();

        const batchStartTime = Date.now();
        const result = await this.replayBatchedStoryUpdates(retryableSupabase, batched?.storyUpdates?.[i]);
        const batchEndTime = Date.now();
        
        // Track replay duration for this batch
        metricsCollector?.trackReplayDuration(
          OPERATION_TYPES?.STORY_UPDATE,
          batchStartTime,
          batchEndTime,
          batched?.storyUpdates?.[i]?.operations?.length,
          result?.success > 0
        );
        
        successCount += result?.success;
        failCount += result?.failed;
        this.batchProgress.processedOps += result?.success + result?.failed;
        this.notifyListeners();
      }

      const endTime = Date.now();
      const duration = ((endTime - startTime) / 1000)?.toFixed(2);

      // Track overall replay duration
      metricsCollector?.trackReplayDuration(
        'all',
        replayStartTime,
        endTime,
        pendingOps?.length,
        successCount > 0
      );

      // Mark batch progress as complete
      this.batchProgress.isActive = false;
      this.batchProgress.lastResult = {
        success: true,
        message: `Synced ${successCount} operations in ${duration}s (saved ${batched?.stats?.savedOperations} operations)`,
      };

    } catch (error) {
      console.error('[OfflineQueue] Batch replay failed:', error);
      this.batchProgress.isActive = false;
      this.batchProgress.lastResult = {
        success: false,
        message: 'Batch replay failed',
      };
    }

    this.isReplaying = false;
    this.notifyListeners();

    // Show notification
    if (successCount > 0) {
      showToast(
        `Successfully synced ${successCount} operations in ${batched?.stats?.batchCount} batches (saved ${batched?.stats?.savedOperations} operations)`,
        'success'
      );
    }

    if (failCount > 0) {
      showToast(
        `Failed to sync ${failCount} operation${failCount > 1 ? 's' : ''}. Will retry later.`,
        'error'
      );
    }

    console.log(`[OfflineQueue] Batch replay complete: ${successCount} success, ${failCount} failed, saved ${batched?.stats?.savedOperations} operations`);
  }

  /**
   * Replay batched clap operations using aggregated counts
   */
  async replayBatchedClaps(client, clapBatch) {
    let success = 0;
    let failed = 0;

    for (const aggregated of clapBatch) {
      try {
        const { storyId, totalIncrement, operations } = aggregated;

        // Fetch current clap count
        const result = await client?.retrySelect(
          'stories',
          'id, clap_count',
          { id: storyId }
        );

        if (result?.data?.length > 0) {
          const currentCount = result?.data?.[0]?.clap_count || 0;
          const newCount = currentCount + totalIncrement;

          // Update with aggregated count
          await client?.retryUpdate(
            'stories',
            { clap_count: newCount },
            { id: storyId }
          );

          // Remove all operations from queue
          operations?.forEach(op => this.dequeue(op?.id));
          success++;

          console.log(`[OfflineQueue] Batched ${operations?.length} claps for story ${storyId}: +${totalIncrement}`);
        }
      } catch (error) {
        console.error('[OfflineQueue] Failed to replay batched claps:', error);
        failed++;
      }
    }

    return { success, failed };
  }

  /**
   * Replay batched bookmark operations
   */
  async replayBatchedBookmarks(client, bookmarkBatch) {
    let success = 0;
    let failed = 0;

    for (const op of bookmarkBatch) {
      try {
        const { operation, data } = op;

        if (operation === OPERATION_ACTIONS?.INSERT) {
          // Check if bookmark already exists
          const existing = await client?.retrySelect(
            'bookmarks',
            'id',
            { user_id: data?.user_id, story_id: data?.story_id }
          );

          if (existing?.data?.length === 0) {
            await client?.retryInsert('bookmarks', data);
          }
        } else if (operation === OPERATION_ACTIONS?.DELETE) {
          await client?.retryDelete('bookmarks', {
            user_id: data?.user_id,
            story_id: data?.story_id,
          });
        }

        this.dequeue(op?.id);
        success++;
      } catch (error) {
        console.error('[OfflineQueue] Failed to replay bookmark:', error);
        failed++;
      }
    }

    return { success, failed };
  }

  /**
   * Replay batched comment operations using bulk insert
   */
  async replayBatchedComments(client, commentBatch) {
    let success = 0;
    let failed = 0;

    // Decompress comment content
    const decompressedComments = commentBatch?.map(op => {
      const data = { ...op?.data };
      if (op?.compressionFlags?.contentCompressed) {
        data.content = decompressText(data?.content, true);
      }
      return { ...op, data };
    });

    // Bulk insert comments
    try {
      const commentsToInsert = decompressedComments?.map(op => op?.data);
      
      if (commentsToInsert?.length > 0) {
        const result = await client?.retryInsert('comments', commentsToInsert);
        
        if (result?.error) {
          throw result?.error;
        }

        // Remove all from queue
        decompressedComments?.forEach(op => this.dequeue(op?.id));
        success = decompressedComments?.length;

        console.log(`[OfflineQueue] Bulk inserted ${success} comments`);
      }
    } catch (error) {
      console.error('[OfflineQueue] Failed to bulk insert comments:', error);
      
      // Fallback to individual inserts
      for (const op of decompressedComments) {
        try {
          await client?.retryInsert('comments', op?.data);
          this.dequeue(op?.id);
          success++;
        } catch (err) {
          console.error('[OfflineQueue] Failed to insert comment:', err);
          failed++;
        }
      }
    }

    return { success, failed };
  }

  /**
   * Replay batched story update operations
   */
  async replayBatchedStoryUpdates(client, storyBatch) {
    let success = 0;
    let failed = 0;

    for (const op of storyBatch) {
      try {
        const { operation, data } = op;
        
        // Decompress content if compressed
        let decompressedData = { ...data };
        if (op?.compressionFlags?.contentCompressed) {
          decompressedData.content = decompressText(data?.content, true);
        }

        if (operation === OPERATION_ACTIONS?.INSERT) {
          const existing = await client?.retrySelect(
            'stories',
            'id',
            { id: decompressedData?.id }
          );

          if (existing?.data?.length > 0) {
            await client?.retryUpdate('stories', decompressedData, { id: decompressedData?.id });
          } else {
            await client?.retryInsert('stories', decompressedData);
          }
        } else if (operation === OPERATION_ACTIONS?.UPDATE) {
          await client?.retryUpdate('stories', decompressedData, { id: decompressedData?.id });
        }

        this.dequeue(op?.id);
        success++;
      } catch (error) {
        console.error('[OfflineQueue] Failed to replay story update:', error);
        failed++;
      }
    }

    return { success, failed };
  }

  /**
   * Replay a single operation with advanced conflict resolution
   */
  async replayOperation(operation) {
    const { operationType, tableName, operation: action, data, metadata } = operation;

    console.log(`[OfflineQueue] Replaying ${operationType} ${action}:`, operation?.id);

    // Mark as replaying
    operation.status = QUEUE_STATUS?.REPLAYING;
    this.notifyListeners();

    // Import supabase dynamically to avoid circular dependency
    const { retryableSupabase } = await import('../lib/supabase');

    try {
      // Fetch current server state
      const serverState = await this.fetchServerState(retryableSupabase, operationType, data);
      operation.originalServerState = serverState;

      // Check if operation is stale
      const staleCheck = isOperationStale(operation, serverState, STALE_THRESHOLD_MS);
      if (staleCheck?.isStale) {
        console.warn(`[OfflineQueue] Operation is stale:`, staleCheck);
        
        // For now, skip stale operations (could prompt user in future)
        return {
          skipped: true,
          reason: 'stale',
          details: staleCheck,
        };
      }

      // Detect conflicts
      const conflict = detectConflict(data, serverState, operationType);
      
      // Apply conflict resolution strategy
      let resolution;
      if (operation?.conflictResolutionStrategy === 'last_write_wins') {
        resolution = CONFLICT_STRATEGIES?.lastWriteWins(data, serverState);
      } else if (operation?.conflictResolutionStrategy === 'server_state_priority') {
        resolution = await CONFLICT_STRATEGIES?.serverStatePriority(data, serverState, operationType);
      } else if (operation?.conflictResolutionStrategy === 'merge_numeric') {
        resolution = CONFLICT_STRATEGIES?.mergeNumericFields(data, serverState, ['clap_count']);
      } else {
        resolution = CONFLICT_STRATEGIES?.lastWriteWins(data, serverState);
      }

      // Log conflict resolution
      if (conflict?.hasConflict) {
        logConflictResolution(operation, resolution, conflict);
        
        // Track conflict metrics
        metricsCollector?.trackConflict(
          operationType,
          conflict?.conflictType || 'data_mismatch',
          operation?.conflictResolutionStrategy,
          tableName
        );
      }

      // Skip if resolution says not to apply
      if (!resolution?.shouldApply) {
        console.log(`[OfflineQueue] Skipping operation due to conflict resolution: ${resolution?.reason}`);
        return {
          skipped: true,
          reason: resolution?.reason,
          conflict: true,
          conflictDetails: conflict,
        };
      }

      // Apply the operation with resolved data
      switch (operationType) {
        case OPERATION_TYPES?.BOOKMARK:
          await this.replayBookmark(retryableSupabase, action, resolution?.data, metadata, serverState);
          break;

        case OPERATION_TYPES?.CLAP:
          await this.replayClap(retryableSupabase, resolution?.data, metadata, serverState);
          break;

        case OPERATION_TYPES?.COMMENT:
          await this.replayComment(retryableSupabase, resolution?.data, metadata, serverState);
          break;

        case OPERATION_TYPES?.STORY_UPDATE:
          await this.replayStoryUpdate(retryableSupabase, action, resolution?.data, metadata, serverState);
          break;

        default:
          throw new Error(`Unknown operation type: ${operationType}`);
      }

      console.log(`[OfflineQueue] Successfully replayed ${operationType}:`, operation?.id);
      
      return {
        success: true,
        conflict: conflict?.hasConflict,
        resolution: resolution?.reason,
      };
    } catch (error) {
      console.error(`[OfflineQueue] Replay failed for ${operationType}:`, error);
      throw error;
    }
  }

  /**
   * Fetch current server state for conflict detection
   */
  async fetchServerState(client, operationType, data) {
    try {
      switch (operationType) {
        case OPERATION_TYPES?.BOOKMARK: {
          const result = await client?.retrySelect(
            'bookmarks',
            '*',
            { user_id: data?.user_id, story_id: data?.story_id }
          );
          return result?.data?.[0] || null;
        }

        case OPERATION_TYPES?.CLAP: {
          const result = await client?.retrySelect(
            'stories',
            'id, clap_count, updated_at',
            { id: data?.story_id }
          );
          return result?.data?.[0] || null;
        }

        case OPERATION_TYPES?.COMMENT: {
          // Check for existing comments with same content
          const result = await client?.retrySelect(
            'comments',
            '*',
            {
              story_id: data?.story_id,
              author_id: data?.author_id,
            }
          );
          
          // Find comment with same content
          const existing = result?.data?.find(c => c?.content === data?.content);
          return existing || null;
        }

        case OPERATION_TYPES?.STORY_UPDATE: {
          const result = await client?.retrySelect(
            'stories',
            '*',
            { id: data?.id }
          );
          return result?.data?.[0] || null;
        }

        default:
          return null;
      }
    } catch (error) {
      console.error('[OfflineQueue] Failed to fetch server state:', error);
      return null;
    }
  }

  /**
   * Replay bookmark operation with enhanced conflict resolution
   */
  async replayBookmark(client, action, data, metadata, serverState) {
    if (action === OPERATION_ACTIONS?.INSERT) {
      // Check if bookmark already exists (idempotent insert)
      const existing = await client?.retrySelect(
        'bookmarks',
        'id',
        { user_id: data?.user_id, story_id: data?.story_id }
      );

      if (existing?.data?.length > 0) {
        console.log('[OfflineQueue] Bookmark already exists, skipping');
        return;
      }

      // Insert bookmark
      await client?.retryInsert('bookmarks', data);
    } else if (action === OPERATION_ACTIONS?.DELETE) {
      // Delete bookmark (idempotent)
      await client?.retryDelete('bookmarks', {
        user_id: data?.user_id,
        story_id: data?.story_id,
      });
    }
  }

  /**
   * Replay clap operation with server-state merging
   */
  async replayClap(client, data, metadata, serverState) {
    // Use resolved data which already has merged clap_count
    await client?.retryUpdate(
      'stories',
      { clap_count: data?.clap_count },
      { id: data?.story_id || data?.id }
    );
  }

  /**
   * Replay comment operation with duplicate detection
   */
  async replayComment(client, data, metadata, serverState) {
    // If serverState exists, it means duplicate was found
    if (serverState) {
      console.log('[OfflineQueue] Duplicate comment detected, skipping');
      return;
    }

    // Insert comment
    await client?.retryInsert('comments', data);
  }

  /**
   * Replay story update operation with field-level merging
   */
  async replayStoryUpdate(client, action, data, metadata, serverState) {
    if (action === OPERATION_ACTIONS?.INSERT) {
      // Check if story already exists
      const existing = await client?.retrySelect(
        'stories',
        'id',
        { id: data?.id }
      );

      if (existing?.data?.length > 0) {
        // Story exists, update instead (last-write-wins)
        await client?.retryUpdate('stories', data, { id: data?.id });
      } else {
        // Insert new story
        await client?.retryInsert('stories', data);
      }
    } else if (action === OPERATION_ACTIONS?.UPDATE) {
      // Fetch current story to check updated_at timestamp
      const result = await client?.retrySelect(
        'stories',
        'updated_at',
        { id: data?.id }
      );

      if (result?.data?.length > 0) {
        const currentUpdatedAt = new Date(result.data[0].updated_at)?.getTime();
        const queuedUpdatedAt = new Date(data.updated_at)?.getTime();

        // Only update if queued version is newer (last-write-wins)
        if (queuedUpdatedAt >= currentUpdatedAt) {
          await client?.retryUpdate('stories', data, { id: data?.id });
        } else {
          console.log('[OfflineQueue] Queued story update is older, skipping');
        }
      }
    }
  }

  /**
   * Get operations requiring manual resolution
   */
  getConflictOperations() {
    return this.queue?.filter(op => op?.status === 'conflict');
  }

  /**
   * Resolve conflict manually
   */
  resolveConflict(operationId, resolution) {
    const op = this.queue?.find(o => o?.id === operationId);
    if (!op) return;

    if (resolution === 'use_queued') {
      op.status = QUEUE_STATUS?.PENDING;
      op.conflictResolutionStrategy = 'force_queued';
    } else if (resolution === 'use_server') {
      this.dequeue(operationId);
    } else if (resolution === 'skip') {
      this.dequeue(operationId);
    }

    this.saveQueue();
    this.notifyListeners();
  }

  /**
   * Register listener for queue changes
   */
  addListener(callback) {
    this.listeners?.push(callback);
    return () => {
      this.listeners = this.listeners?.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners() {
    const state = {
      isOnline: this.isOnline,
      connectionQualityGood: this.connectionQualityGood,
      pendingCount: this.getPendingCount(),
      isReplaying: this.isReplaying,
      queue: this.getQueue(),
      batchProgress: this.batchProgress,
    };

    this.listeners?.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('[OfflineQueue] Listener error:', error);
      }
    });
  }

  /**
   * Get current state with batch progress
   */
  getState() {
    return {
      isOnline: this.isOnline,
      connectionQualityGood: this.connectionQualityGood,
      pendingCount: this.getPendingCount(),
      isReplaying: this.isReplaying,
      queue: this.getQueue(),
      batchProgress: this.batchProgress,
    };
  }
}

// Create singleton instance
export const offlineQueueManager = new OfflineQueueManager();

// Auto-replay on page load if online
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(() => {
      offlineQueueManager?.replayQueue();
    }, 2000); // Wait 2 seconds after page load
  });
}
